import type { Writable } from "svelte/store";
import type { MegaConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Mega encrypts names and file contents with the account's keys. The server
// does that crypto, so file calls go through /api/mega. A login trades the
// password for a session id and the account's master key. Those two are
// request headers, not query parameters.

class FileSystemMega extends FS {
  supportsDirRename = true;

  constructor(config: MegaConfig) {
    super(config);
  }

  get megaConfig(): MegaConfig {
    return this.config as MegaConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await megaCall(megaHeaders(this.megaConfig), mkURL("/api/mega/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("mega list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the key headers, and the file
    // is decrypted on the way. Fetch through the same-origin proxy and hand the
    // bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/mega/download", "path", path);
    let xhr = await megaSend("GET", url, this.megaConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/mega/upload", "path", path);
    await megaSend("PUT", url, this.megaConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/mega/rename", "path", path, "newPath", newPath);
    await megaCall(megaHeaders(this.megaConfig), url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = mkURL("/api/mega/mkdir", "path", path);
    await megaCall(megaHeaders(this.megaConfig), url, "POST");
  }

  // The server moves the entry to Mega's rubbish bin.
  async deleteFile(path: string): Promise<void> {
    let url = mkURL("/api/mega/delete", "path", path);
    await megaCall(megaHeaders(this.megaConfig), url, "POST");
  }
}

/**
 * Log in with an email and password. code is the two-factor code, empty for
 * accounts without one. Returns the session id and the master key.
 */
export async function megaLogin(email: string, password: string, code: string): Promise<{ sid: string; key: string }> {
  let headers: Record<string, string> = {
    "X-Mega-Email": encodeSecret(email),
    "X-Mega-Password": encodeSecret(password),
  };
  if (code) {
    headers["X-Mega-Code"] = encodeSecret(code);
  }
  let js = await megaCall(headers, "/api/mega/login", "POST");
  throwIf(!js.sid || !js.key, "mega login failed");
  return { sid: js.sid, key: js.key };
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function megaHeaders(config: MegaConfig): Record<string, string> {
  return {
    "X-Mega-Sid": config.sid,
    "X-Mega-Key": config.key,
  };
}

async function megaCall(headers: Record<string, string>, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, { method, headers });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `mega ${rsp.status}`);
  }
  return js;
}

function megaSend(
  method: string,
  url: string,
  config: MegaConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = megaHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      megaErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`mega ${xhr.status}`)),
      );
    };
    // also fires when the server cuts a download short because the file
    // failed Mega's integrity check
    xhr.onerror = () => {
      reject(new Error("mega request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function megaErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `mega ${xhr.status}`;
}

export function makeFileSystemMega(config: MegaConfig): FileSystemMega {
  return new FileSystemMega(config);
}
